import React, { useCallback, useMemo } from "react";

import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useInvestorsSelector } from "store";
import { ISaveDealAsTemplateData } from "store/types/templates";

import { PickerFilterBar } from "./PickerFilterBar";
import { PickerHeader } from "./PickerHeader";
import { SelectionPanel } from "./SelectionPanel";
import { border, s } from "../styles";
import { TemplateTreeView } from "./TemplateTreeView";
import { PickerModeConfig } from "../hooks/use-picker-filters";
import { useTemplateSelection } from "../hooks/use-template-selection";

const TEMPLATE_CONFIG: PickerModeConfig = {
  title: "Save as template",
  subtitle: "Choose the folders and files new deals should inherit",
  showTemplateName: true,
  foldersSelectable: true,
  showViewToggle: false,
  showUploadButton: false,
  showMagicButton: false,
  confirmLabel: () => "Save as template"
};

/* ─── Template mode props ─── */
export interface TemplatePickerProps {
  mode: "template";
  onSubmit: (result: { templateName: string; sections: ISaveDealAsTemplateData["sections"] }) => void;
  onCancel: () => void;
}

export const TemplatePicker: React.FC<TemplatePickerProps> = ({ onSubmit, onCancel }) => {
  const config = TEMPLATE_CONFIG;
  const selection = useTemplateSelection();
  const investors = useInvestorsSelector();
  const investorMap = useMemo(() => {
    const map = new Map<number, string>();
    investors.forEach((inv) => map.set(inv.id, inv.name));
    return map;
  }, [investors]);

  const handleSubmit = useCallback(() => {
    if (!selection.state.folders.length) return;
    onSubmit({
      templateName: selection.templateName,
      sections: selection.buildPayload()
    });
  }, [selection, onSubmit]);

  const isDisabled = !selection.state.folders.length || !selection.templateName.trim();

  return (
    <Box
      sx={{
        width: "90vw",
        maxWidth: 1100,
        maxHeight: "calc(100vh - 48px)",
        bgcolor: "background.paper",
        borderRadius: "12px",
        border: border(),
        boxShadow: "0px 10px 10px -5px rgba(18, 18, 23, 0.04), 0px 20px 25px -5px rgba(18, 18, 23, 0.10)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        outline: "none"
      }}
    >
      {/* Header */}
      <PickerHeader title={config.title} subtitle={config.subtitle} onClose={onCancel} />

      {/* Template name */}
      <Box sx={{ px: "20px", pt: "16px", pb: "10px" }}>
        <TextField
          fullWidth
          size="small"
          id="picker-template-name"
          label="Template Name"
          placeholder="Name this template"
          value={selection.templateName}
          onChange={(e) => selection.setTemplateName(e.target.value)}
          sx={{ "& .MuiInputBase-root": { height: 32 } }}
        />
      </Box>

      {/* Controls - Filters + Search */}
      <PickerFilterBar
        filters={selection.filters}
        hasActiveFilters={selection.hasActiveFilters}
        classificationOptions={selection.classificationOptions}
        setClassifications={selection.setClassifications}
        setInvestors={selection.setInvestors}
        setEffectiveDate={selection.setEffectiveDate}
        clearFilter={selection.clearFilter}
        clearAllFilters={selection.clearAllFilters}
      >
        <TextField
          size="small"
          placeholder="Search file name"
          value={selection.searchQuery}
          onChange={(e) => selection.setSearchQuery(e.target.value)}
          sx={{ width: 232, flexShrink: 0, "& .MuiInputBase-root": { height: 32 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
              </InputAdornment>
            )
          }}
        />
      </PickerFilterBar>

      {/* Results summary */}
      {(selection.hasActiveFilters || selection.searchQuery) && (
        <Box sx={{ px: "20px", py: "8px", borderBottom: border() }}>
          <Typography sx={{ fontSize: 15 }}>
            Filter result: <strong>{selection.filteredFileCount} files</strong>
          </Typography>
        </Box>
      )}

      {/* Content area */}
      <Box sx={s.contentArea}>
        <TemplateTreeView selection={selection} investorMap={investorMap} />
      </Box>

      {/* Selection panel */}
      <SelectionPanel mode="template" selection={selection} />

      {/* Footer */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        gap="10px"
        sx={{ px: "20px", py: "14px", borderTop: border() }}
      >
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" disabled={isDisabled} onClick={handleSubmit}>
          {config.confirmLabel(selection.selectedCount)}
        </Button>
      </Stack>
    </Box>
  );
};

/* ─── Prompt-compatible template picker ─── */
export interface TemplatePickerPromptProps {
  onSubmit: (result: { templateName: string; sections: ISaveDealAsTemplateData["sections"] }) => void;
  onCancel: () => void;
}

export const TemplateContentPicker: React.FC<TemplatePickerPromptProps> = ({ onSubmit, onCancel }) => {
  return <TemplatePicker mode="template" onSubmit={onSubmit} onCancel={onCancel} />;
};
